import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, FileText, MessageSquare, Phone, Building2, Loader2, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuth } from "@/contexts/AuthContext";
import { clinicsGraphqlService } from "@/services/api/clinics/clinicsGraphqlService";
import { addPatientService, AddPatientPayload } from "@/services/features/AddPatientService";

interface ClinicOption {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
}

interface FacilityOption {
  facility_id: number;
  facility_name: string;
}

interface ProviderOption {
  staff_id: number;
  staff_name: string;
}

interface PatientFormData {
  // Clinic Selection
  clinicId: string;
  facilityId: string;
  providerId: string;
  // Personal Information
  firstName: string;
  lastName: string;
  aliasName: string;
  email: string;
  phoneNumber: string;
  alternatePhoneNumber: string;
  birthDate: string;
  gender: string;
  heightFeet: string;
  heightInches: string;
  // Medical Records
  emrId: string;
  primaryInsuranceId: string;
  primaryInsurancePayer: string;
  secondaryInsuranceId: string;
  secondaryInsurancePayer: string;
  insurancePlan: string;
  // Program Eligibility
  isCcmEligible: boolean;
  isRpmEligible: boolean;
  isPcmEligible: boolean;
  // Communication Preference
  languagePreference: string;
  // Emergency Contact
  emergencyFirstName: string;
  emergencyLastName: string;
  emergencyPhoneNumber: string;
  // Address (Step 2)
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const initialFormData: PatientFormData = {
  clinicId: "",
  facilityId: "",
  providerId: "",
  firstName: "",
  lastName: "",
  aliasName: "",
  email: "",
  phoneNumber: "",
  alternatePhoneNumber: "",
  birthDate: "",
  gender: "",
  heightFeet: "",
  heightInches: "",
  emrId: "",
  primaryInsuranceId: "",
  primaryInsurancePayer: "",
  secondaryInsuranceId: "",
  secondaryInsurancePayer: "",
  insurancePlan: "",
  isCcmEligible: false,
  isRpmEligible: false,
  isPcmEligible: false,
  languagePreference: "English",
  emergencyFirstName: "",
  emergencyLastName: "",
  emergencyPhoneNumber: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
  country: "USA",
};

const steps = [
  { id: 1, label: "Patient Details" },
  { id: 2, label: "Address" },
  { id: 3, label: "Summary" },
];

const AddPatientPage = () => {
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useErrorHandler();
  const { hasuraToken, token, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<PatientFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  
  // Clinic/Facility/Provider state
  const [clinics, setClinics] = useState<ClinicOption[]>([]);
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);

  // Set browser tab title
  useEffect(() => {
    document.title = "Add Patient | Admin";
  }, []);

  // Fetch clinics on mount
  useEffect(() => {
    const fetchClinics = async () => {
      if (!hasuraToken) return;
      setIsLoadingClinics(true);
      setIsPageLoading(true);
      try {
        const data = await clinicsGraphqlService.getAvailableClinics(hasuraToken);
        setClinics(data);
      } catch (error) {
        console.error('Error fetching clinics:', error);
      } finally {
        setIsLoadingClinics(false);
        setIsPageLoading(false);
      }
    };
    fetchClinics();
  }, [hasuraToken]);

  // Fetch facilities when clinic changes
  useEffect(() => {
    const fetchFacilities = async () => {
      if (!hasuraToken || !formData.clinicId) {
        setFacilities([]);
        return;
      }
      setIsLoadingFacilities(true);
      try {
        const data = await clinicsGraphqlService.getFacilitiesByClinic(
          parseInt(formData.clinicId),
          hasuraToken
        );
        setFacilities(data);
      } catch (error) {
        console.error('Error fetching facilities:', error);
      } finally {
        setIsLoadingFacilities(false);
      }
    };
    fetchFacilities();
  }, [hasuraToken, formData.clinicId]);

  // Fetch providers when clinic changes
  useEffect(() => {
    const fetchProviders = async () => {
      if (!hasuraToken || !formData.clinicId) {
        setProviders([]);
        return;
      }
      setIsLoadingProviders(true);
      try {
        const data = await clinicsGraphqlService.getProvidersByClinicFromEMR(
          parseInt(formData.clinicId),
          hasuraToken
        );
        setProviders(data);
      } catch (error) {
        console.error('Error fetching providers:', error);
      } finally {
        setIsLoadingProviders(false);
      }
    };
    fetchProviders();
  }, [hasuraToken, formData.clinicId]);

  const updateFormData = (field: keyof PatientFormData, value: string) => {
    // Reset dependent fields when clinic changes
    if (field === 'clinicId') {
      setFormData((prev) => ({ 
        ...prev, 
        [field]: value,
        facilityId: "",
        providerId: ""
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const updateBooleanFormData = (field: 'isCcmEligible' | 'isRpmEligible' | 'isPcmEligible', value: boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    if (!email.trim()) return true; // Email is optional, empty is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Phone number validation helper - accepts formats: (308)-135-7895, 308-135-7895, 308135-7895, 3081357895
  const isValidPhoneFormat = (phone: string): boolean => {
    if (!phone.trim()) return false; // Phone is required
    // Remove all non-digit characters to check length
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Only validate when we have 10 digits worth of input
    if (digitsOnly.length < 10) return true; // Still typing, don't show error yet
    if (digitsOnly.length > 10) return false; // Too many digits
    
    // Accepted patterns:
    // (XXX)-XXX-XXXX
    // XXX-XXX-XXXX
    // XXXXXX-XXXX
    // XXXXXXXXXX
    const phoneRegex = /^(\(\d{3}\)-\d{3}-\d{4}|\d{3}-\d{3}-\d{4}|\d{6}-\d{4}|\d{10})$/;
    return phoneRegex.test(phone.trim());
  };

  // Check if phone has enough digits to validate (for showing error only when complete)
  const shouldValidatePhone = (phone: string): boolean => {
    if (!phone.trim()) return false;
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };

  // Alternate/Emergency phone validation (optional, but if provided must be valid format)
  const isValidOptionalPhoneFormat = (phone: string): boolean => {
    if (!phone.trim()) return true; // Optional phone is valid when empty
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Only validate when we have 10 digits worth of input
    if (digitsOnly.length < 10) return true; // Still typing, don't show error yet
    if (digitsOnly.length > 10) return false; // Too many digits
    
    const phoneRegex = /^(\(\d{3}\)-\d{3}-\d{4}|\d{3}-\d{3}-\d{4}|\d{6}-\d{4}|\d{10})$/;
    return phoneRegex.test(phone.trim());
  };

  // DOB validation helper - must be 18 years or older
  const isValidAge = (birthDate: string): boolean => {
    if (!birthDate) return false;
    const today = new Date();
    const dob = new Date(birthDate);
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age >= 18;
  };

  // Validation for Step 1: Patient Details
  const isStep1Valid = (): boolean => {
    return !!(
      formData.clinicId &&
      formData.facilityId &&
      formData.providerId &&
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.phoneNumber.trim() &&
      isValidPhoneFormat(formData.phoneNumber) &&
      isValidOptionalPhoneFormat(formData.alternatePhoneNumber) &&
      formData.birthDate &&
      isValidAge(formData.birthDate) &&
      formData.gender &&
      formData.emrId.trim() &&
      isValidEmail(formData.email) &&
      formData.primaryInsuranceId.trim() &&
      formData.primaryInsurancePayer.trim() &&
      formData.insurancePlan.trim()
    );
  };

  // Validation for Step 2: Address
  const isStep2Valid = (): boolean => {
    return !!(
      formData.addressLine1.trim() &&
      formData.city.trim() &&
      formData.state.trim() &&
      formData.zipCode.trim()
    );
  };

  // Check if current step is valid
  const isCurrentStepValid = (): boolean => {
    if (currentStep === 1) return isStep1Valid();
    if (currentStep === 2) return isStep2Valid();
    return true;
  };

  const handleNext = () => {
    if (!isCurrentStepValid()) {
      return; // Prevent navigation if validation fails
    }
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!token || !user?.id) {
      handleError(new Error("Authentication required"), "Please log in to add a patient");
      return;
    }

    setIsSubmitting(true);
    try {
      // Build emergency contact name from first and last name
      const emergencyContactName = [formData.emergencyFirstName, formData.emergencyLastName]
        .filter(Boolean)
        .join(' ');

      // Map gender to API format (M/F/O)
      const genderMap: Record<string, string> = {
        'Male': 'M',
        'Female': 'F',
        'Other': 'O'
      };

      const payload: AddPatientPayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dob: formData.birthDate,
        gender: genderMap[formData.gender] || formData.gender,
        emrId: formData.emrId,
        clinicFacilityId: parseInt(formData.facilityId),
        clinicId: parseInt(formData.clinicId),
        providerId: parseInt(formData.providerId),
        addressLineOne: formData.addressLine1,
        addressLineTwo: formData.addressLine2 || undefined,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        email: formData.email || undefined,
        mobileNumber: formData.phoneNumber,
        homeNumber: formData.alternatePhoneNumber || undefined,
        emergencyContactName: emergencyContactName || undefined,
        emergencyContactPhone: formData.emergencyPhoneNumber || undefined,
        primaryInsuranceProvider: formData.primaryInsurancePayer || undefined,
        primaryInsurancePolicyNumber: formData.primaryInsuranceId || undefined,
        secondaryInsuranceProvider: formData.secondaryInsurancePayer || undefined,
        secondaryInsurancePolicyNumber: formData.secondaryInsuranceId || undefined,
        isActive: true,
        // Only include eligibility flags if true
        ...(formData.isCcmEligible && { isCcmEligible: true }),
        ...(formData.isRpmEligible && { isRpmEligible: true }),
        ...(formData.isPcmEligible && { isPcmEligible: true }),
      };

      const response = await addPatientService.addPatient(user.id, payload, token);

      if (response.success) {
        handleSuccess("Patient added successfully!", "Success");
        navigate(-1);
      } else {
        handleError(new Error(response.error || "Failed to add patient"), "Failed to add patient");
      }
    } catch (error) {
      handleError(error, "Failed to add patient");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading screen while initial data is loading
  if (isPageLoading) {
    return (
      <div id="add_patient_loading" className="flex-1 flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-white">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 text-purple-600 animate-spin" />
          <p className="text-lg font-medium text-purple-600">Loading Add Patient...</p>
        </div>
      </div>
    );
  }

  return (
    <div id="add_patient_page_container" className="flex-1 bg-gradient-to-br from-purple-50 via-pink-50 to-white overflow-auto">

      {/* Stepper */}
      <div id="add_patient_stepper" className="flex justify-center items-center py-8 px-6">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all ${
                  currentStep >= step.id
                    ? "bg-purple-600 border-purple-600 text-white"
                    : "bg-white border-gray-300 text-gray-400"
                }`}
              >
                {step.id}
              </div>
              <span
                className={`mt-2 text-sm font-medium ${
                  currentStep >= step.id ? "text-purple-600" : "text-gray-400"
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-24 h-0.5 mx-4 ${
                  currentStep > step.id ? "bg-purple-600" : "bg-gray-300"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Form Content */}
      <div className="max-w-6xl mx-auto px-6 pb-8">
        {currentStep === 1 && (
          <PatientDetailsStep 
            formData={formData} 
            updateFormData={updateFormData}
            updateBooleanFormData={updateBooleanFormData}
            clinics={clinics}
            facilities={facilities}
            providers={providers}
            isLoadingClinics={isLoadingClinics}
            isLoadingFacilities={isLoadingFacilities}
            isLoadingProviders={isLoadingProviders}
            isValidEmail={isValidEmail}
            isValidAge={isValidAge}
            isValidPhoneFormat={isValidPhoneFormat}
            isValidOptionalPhoneFormat={isValidOptionalPhoneFormat}
            shouldValidatePhone={shouldValidatePhone}
          />
        )}
        {currentStep === 2 && (
          <AddressStep formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 3 && <SummaryStep formData={formData} />}

        {/* Navigation Buttons */}
        <div className="flex gap-4 mt-8">
          {currentStep > 1 && (
            <Button
              id="add_patient_previous_btn"
              onClick={handlePrevious}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6"
            >
              Previous
            </Button>
          )}
          {currentStep < 3 ? (
            <Button
              id="add_patient_continue_btn"
              onClick={handleNext}
              disabled={!isCurrentStepValid()}
              className="bg-purple-400 hover:bg-purple-500 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </Button>
          ) : (
            <Button
              id="add_patient_submit_btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700 text-white px-6"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Step 1: Patient Details
const PatientDetailsStep = ({
  formData,
  updateFormData,
  clinics,
  facilities,
  providers,
  isLoadingClinics,
  isLoadingFacilities,
  isLoadingProviders,
  isValidEmail,
  isValidAge,
  isValidPhoneFormat,
  isValidOptionalPhoneFormat,
  shouldValidatePhone,
  updateBooleanFormData,
}: {
  formData: PatientFormData;
  updateFormData: (field: keyof PatientFormData, value: string) => void;
  updateBooleanFormData: (field: 'isCcmEligible' | 'isRpmEligible' | 'isPcmEligible', value: boolean) => void;
  clinics: ClinicOption[];
  facilities: FacilityOption[];
  providers: ProviderOption[];
  isLoadingClinics: boolean;
  isLoadingFacilities: boolean;
  isLoadingProviders: boolean;
  isValidEmail: (email: string) => boolean;
  isValidAge: (birthDate: string) => boolean;
  isValidPhoneFormat: (phone: string) => boolean;
  isValidOptionalPhoneFormat: (phone: string) => boolean;
  shouldValidatePhone: (phone: string) => boolean;
}) => {
  const [clinicOpen, setClinicOpen] = useState(false);
  const selectedClinic = clinics.find(c => c.clinic_id.toString() === formData.clinicId);

  return (
    <div className="space-y-8">

      {/* Clinic Selection */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-pink-600">
          <Building2 className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Clinic Selection</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="clinicId" className="text-gray-700">
              Select Clinic <span className="text-red-500">*</span>
            </Label>
            <Popover open={clinicOpen} onOpenChange={setClinicOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="add_patient_clinic"
                  variant="outline"
                  role="combobox"
                  aria-expanded={clinicOpen}
                  className="mt-1 w-full justify-between bg-purple-100 border-purple-300 hover:bg-purple-50 text-left font-normal overflow-hidden"
                >
                  <span className="truncate">
                    {isLoadingClinics 
                      ? "Loading..." 
                      : selectedClinic 
                        ? selectedClinic.clinic_name 
                        : "Select Clinic"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0 bg-white z-50" align="start">
                <Command>
                  <CommandInput placeholder="Search clinic..." />
                  <CommandList>
                    <CommandEmpty>No clinic found.</CommandEmpty>
                    <CommandGroup>
                      {clinics.map((clinic) => (
                        <CommandItem
                          key={clinic.clinic_id}
                          value={clinic.clinic_name}
                          onSelect={() => {
                            updateFormData("clinicId", clinic.clinic_id.toString());
                            setClinicOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              formData.clinicId === clinic.clinic_id.toString() ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {clinic.clinic_name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label htmlFor="facilityId" className="text-gray-700">
              Select Facility <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={formData.facilityId} 
              onValueChange={(value) => updateFormData("facilityId", value)}
              disabled={!formData.clinicId}
            >
              <SelectTrigger 
                id="add_patient_facility" 
                className={`mt-1 truncate ${!formData.clinicId ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : 'bg-purple-100 border-purple-300'}`}
              >
                <SelectValue placeholder={
                  !formData.clinicId 
                    ? "Select Clinic First" 
                    : isLoadingFacilities 
                      ? "Loading..." 
                      : "Select Facility"
                } />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                {facilities.map((facility) => (
                  <SelectItem key={facility.facility_id} value={facility.facility_id.toString()}>
                    {facility.facility_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="providerId" className="text-gray-700">
              Select Provider <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={formData.providerId} 
              onValueChange={(value) => updateFormData("providerId", value)}
              disabled={!formData.clinicId}
            >
              <SelectTrigger 
                id="add_patient_provider" 
                className={`mt-1 truncate ${!formData.clinicId ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : 'bg-purple-100 border-purple-300'}`}
              >
                <SelectValue placeholder={
                  !formData.clinicId 
                    ? "Select Clinic First" 
                    : isLoadingProviders 
                      ? "Loading..." 
                      : "Select Provider"
                } />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                {providers.map((provider) => (
                  <SelectItem key={provider.staff_id} value={provider.staff_id.toString()}>
                    {provider.staff_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-pink-600">
          <User className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Personal Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="firstName" className="text-gray-700">
              First Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="add_patient_first_name"
              value={formData.firstName}
              onChange={(e) => updateFormData("firstName", e.target.value)}
              placeholder="Enter patient first name"
              maxLength={50}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="lastName" className="text-gray-700">
              Last Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="add_patient_last_name"
              value={formData.lastName}
              onChange={(e) => updateFormData("lastName", e.target.value)}
              placeholder="Enter patient last name"
              maxLength={50}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="aliasName" className="text-gray-700">
              Alias Name
            </Label>
            <Input
              id="add_patient_alias_name"
              value={formData.aliasName}
              onChange={(e) => updateFormData("aliasName", e.target.value)}
              placeholder="Enter patient alias name"
              maxLength={50}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-gray-700">
              Email
            </Label>
            <Input
              id="add_patient_email"
              type="email"
              value={formData.email}
              onChange={(e) => updateFormData("email", e.target.value)}
              placeholder="Enter email"
              maxLength={100}
              className={`mt-1 bg-purple-50 border-purple-200 focus:border-purple-400 ${
                formData.email && !isValidEmail(formData.email) ? 'border-red-500' : ''
              }`}
            />
            {formData.email && !isValidEmail(formData.email) && (
              <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
            )}
          </div>
          <div>
            <Label htmlFor="phoneNumber" className="text-gray-700">
              Phone Number <span className="text-red-500">*</span>
            </Label>
            <div className="flex mt-1">
              <span className="inline-flex items-center px-3 bg-purple-100 border border-r-0 border-purple-200 rounded-l-md text-gray-600 text-sm">
                +1
              </span>
              <Input
                id="add_patient_phone_number"
                value={formData.phoneNumber}
                onChange={(e) => updateFormData("phoneNumber", e.target.value)}
                placeholder="eg: 717-827-7017"
                maxLength={15}
                className={`rounded-l-none bg-purple-50 border-purple-200 focus:border-purple-400 ${
                  shouldValidatePhone(formData.phoneNumber) && !isValidPhoneFormat(formData.phoneNumber) ? 'border-red-500' : ''
                }`}
              />
            </div>
            {shouldValidatePhone(formData.phoneNumber) && !isValidPhoneFormat(formData.phoneNumber) && (
              <p className="text-red-500 text-xs mt-1">
                Accepted formats: (308)-135-7895 or 308-135-7895 or 308135-7895 or 3081357895
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="alternatePhoneNumber" className="text-gray-700">
              Alternate Phone Number
            </Label>
            <div className="flex mt-1">
              <span className="inline-flex items-center px-3 bg-purple-100 border border-r-0 border-purple-200 rounded-l-md text-gray-600 text-sm">
                +1
              </span>
              <Input
                id="add_patient_alternate_phone"
                value={formData.alternatePhoneNumber}
                onChange={(e) => updateFormData("alternatePhoneNumber", e.target.value)}
                placeholder="eg: 717-827-7017"
                maxLength={15}
                className={`rounded-l-none bg-purple-50 border-purple-200 focus:border-purple-400 ${
                  shouldValidatePhone(formData.alternatePhoneNumber) && !isValidOptionalPhoneFormat(formData.alternatePhoneNumber) ? 'border-red-500' : ''
                }`}
              />
            </div>
            {shouldValidatePhone(formData.alternatePhoneNumber) && !isValidOptionalPhoneFormat(formData.alternatePhoneNumber) && (
              <p className="text-red-500 text-xs mt-1">
                Accepted formats: (308)-135-7895 or 308-135-7895 or 308135-7895 or 3081357895
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="birthDate" className="text-gray-700">
              Birth Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="add_patient_birth_date"
              type="date"
              value={formData.birthDate}
              onChange={(e) => updateFormData("birthDate", e.target.value)}
              className={`mt-1 bg-purple-100 border-purple-300 focus:border-purple-400 ${
                formData.birthDate && !isValidAge(formData.birthDate) ? 'border-red-500' : ''
              }`}
            />
            {formData.birthDate && !isValidAge(formData.birthDate) && (
              <p className="text-red-500 text-xs mt-1">Patient must be 18 years or older</p>
            )}
          </div>
          <div>
            <Label htmlFor="gender" className="text-gray-700">
              Gender <span className="text-red-500">*</span>
            </Label>
            <Select value={formData.gender} onValueChange={(value) => updateFormData("gender", value)}>
              <SelectTrigger id="add_patient_gender" className="mt-1 bg-purple-100 border-purple-300">
                <SelectValue placeholder="Select Gender" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-700">Height (ft/in)</Label>
            <div className="flex gap-2 mt-1">
              <Select value={formData.heightFeet} onValueChange={(value) => updateFormData("heightFeet", value)}>
                <SelectTrigger id="add_patient_height_feet" className="bg-purple-100 border-purple-300">
                  <SelectValue placeholder="Feet" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  {[3, 4, 5, 6, 7].map((ft) => (
                    <SelectItem key={ft} value={ft.toString()}>{ft}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={formData.heightInches} onValueChange={(value) => updateFormData("heightInches", value)}>
                <SelectTrigger id="add_patient_height_inches" className="bg-purple-100 border-purple-300">
                  <SelectValue placeholder="Inches" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((inch) => (
                    <SelectItem key={inch} value={String(inch)}>{String(inch)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Medical Records */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-pink-600">
          <FileText className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Medical Records</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="emrId" className="text-gray-700">
              EMR ID <span className="text-red-500">*</span>
            </Label>
            <Input
              id="add_patient_emr_id"
              value={formData.emrId}
              onChange={(e) => updateFormData("emrId", e.target.value)}
              placeholder="Enter patient EMR ID"
              maxLength={50}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="primaryInsuranceId" className="text-gray-700">
              Primary Insurance ID <span className="text-red-500">*</span>
            </Label>
            <Input
              id="add_patient_primary_insurance_id"
              value={formData.primaryInsuranceId}
              onChange={(e) => updateFormData("primaryInsuranceId", e.target.value)}
              placeholder="Enter Insurance ID"
              maxLength={50}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="primaryInsurancePayer" className="text-gray-700">
              Primary Insurance Payer <span className="text-red-500">*</span>
            </Label>
            <Input
              id="add_patient_primary_insurance_payer"
              value={formData.primaryInsurancePayer}
              onChange={(e) => updateFormData("primaryInsurancePayer", e.target.value)}
              placeholder="Enter Payer Name"
              maxLength={100}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="secondaryInsuranceId" className="text-gray-700">
              Secondary Insurance ID
            </Label>
            <Input
              id="add_patient_secondary_insurance_id"
              value={formData.secondaryInsuranceId}
              onChange={(e) => updateFormData("secondaryInsuranceId", e.target.value)}
              placeholder="Enter Insurance ID"
              maxLength={50}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="secondaryInsurancePayer" className="text-gray-700">
              Secondary Insurance Payer
            </Label>
            <Input
              id="add_patient_secondary_insurance_payer"
              value={formData.secondaryInsurancePayer}
              onChange={(e) => updateFormData("secondaryInsurancePayer", e.target.value)}
              placeholder="Enter Payer Name"
              maxLength={100}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="insurancePlan" className="text-gray-700">
              Insurance plan <span className="text-red-500">*</span>
            </Label>
            <Input
              id="add_patient_insurance_plan"
              value={formData.insurancePlan}
              onChange={(e) => updateFormData("insurancePlan", e.target.value)}
              placeholder="Enter Insurance plan"
              maxLength={100}
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
        </div>

        {/* Program Eligibility Checkboxes */}
        <div className="mt-4">
          <Label className="text-gray-700 mb-2 block">Program Eligibility</Label>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="add_patient_ccm_eligible"
                checked={formData.isCcmEligible}
                onCheckedChange={(checked) => updateBooleanFormData("isCcmEligible", checked === true)}
                className="border-purple-300 data-[state=checked]:bg-purple-600"
              />
              <Label htmlFor="add_patient_ccm_eligible" className="text-gray-700 cursor-pointer">
                CCM Eligible
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="add_patient_rpm_eligible"
                checked={formData.isRpmEligible}
                onCheckedChange={(checked) => updateBooleanFormData("isRpmEligible", checked === true)}
                className="border-purple-300 data-[state=checked]:bg-purple-600"
              />
              <Label htmlFor="add_patient_rpm_eligible" className="text-gray-700 cursor-pointer">
                RPM Eligible
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="add_patient_pcm_eligible"
                checked={formData.isPcmEligible}
                onCheckedChange={(checked) => updateBooleanFormData("isPcmEligible", checked === true)}
                className="border-purple-300 data-[state=checked]:bg-purple-600"
              />
              <Label htmlFor="add_patient_pcm_eligible" className="text-gray-700 cursor-pointer">
                PCM Eligible
              </Label>
            </div>
          </div>
        </div>
      </div>

      {/* Communication Preference */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-pink-600">
          <MessageSquare className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Communication Preference</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="languagePreference" className="text-gray-700">
              Language Preference
            </Label>
            <Select
              value={formData.languagePreference}
              onValueChange={(value) => updateFormData("languagePreference", value)}
            >
              <SelectTrigger id="add_patient_language" className="mt-1 bg-purple-100 border-purple-300">
                <SelectValue placeholder="Select Language" />
              </SelectTrigger>
              <SelectContent className="bg-white z-50">
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Spanish">Spanish</SelectItem>
                <SelectItem value="French">French</SelectItem>
                <SelectItem value="German">German</SelectItem>
                <SelectItem value="Chinese">Chinese</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Emergency Contact Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-pink-600">
          <Phone className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Emergency Contact Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="emergencyFirstName" className="text-gray-700">
              First Name
            </Label>
            <Input
              id="add_patient_emergency_first_name"
              value={formData.emergencyFirstName}
              onChange={(e) => updateFormData("emergencyFirstName", e.target.value)}
              placeholder="Enter first name"
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="emergencyLastName" className="text-gray-700">
              Last Name
            </Label>
            <Input
              id="add_patient_emergency_last_name"
              value={formData.emergencyLastName}
              onChange={(e) => updateFormData("emergencyLastName", e.target.value)}
              placeholder="Enter last name"
              className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
            />
          </div>
          <div>
            <Label htmlFor="emergencyPhoneNumber" className="text-gray-700">
              Phone Number
            </Label>
            <div className="flex mt-1">
              <span className="inline-flex items-center px-3 bg-purple-100 border border-r-0 border-purple-200 rounded-l-md text-gray-600 text-sm">
                +1
              </span>
              <Input
                id="add_patient_emergency_phone"
                value={formData.emergencyPhoneNumber}
                onChange={(e) => updateFormData("emergencyPhoneNumber", e.target.value)}
                placeholder="eg: 717-827-7017"
                maxLength={15}
                className={`rounded-l-none bg-purple-50 border-purple-200 focus:border-purple-400 ${
                  shouldValidatePhone(formData.emergencyPhoneNumber) && !isValidOptionalPhoneFormat(formData.emergencyPhoneNumber) ? 'border-red-500' : ''
                }`}
              />
            </div>
            {shouldValidatePhone(formData.emergencyPhoneNumber) && !isValidOptionalPhoneFormat(formData.emergencyPhoneNumber) && (
              <p className="text-red-500 text-xs mt-1">
                Accepted formats: (308)-135-7895 or 308-135-7895 or 308135-7895 or 3081357895
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 2: Address
const AddressStep = ({
  formData,
  updateFormData,
}: {
  formData: PatientFormData;
  updateFormData: (field: keyof PatientFormData, value: string) => void;
}) => {
  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-900">Address Information</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label htmlFor="addressLine1" className="text-gray-700">
            Address Line 1 <span className="text-red-500">*</span>
          </Label>
          <Input
            id="add_patient_address_line1"
            value={formData.addressLine1}
            onChange={(e) => updateFormData("addressLine1", e.target.value)}
            placeholder="Enter street address"
            maxLength={200}
            className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="addressLine2" className="text-gray-700">
            Address Line 2
          </Label>
          <Input
            id="add_patient_address_line2"
            value={formData.addressLine2}
            onChange={(e) => updateFormData("addressLine2", e.target.value)}
            placeholder="Apartment, suite, unit, etc."
            maxLength={200}
            className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
          />
        </div>
        <div>
          <Label htmlFor="city" className="text-gray-700">
            City <span className="text-red-500">*</span>
          </Label>
          <Input
            id="add_patient_city"
            value={formData.city}
            onChange={(e) => updateFormData("city", e.target.value)}
            placeholder="Enter city"
            maxLength={100}
            className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
          />
        </div>
        <div>
          <Label htmlFor="state" className="text-gray-700">
            State <span className="text-red-500">*</span>
          </Label>
          <Input
            id="add_patient_state"
            value={formData.state}
            onChange={(e) => updateFormData("state", e.target.value)}
            placeholder="Enter state"
            maxLength={50}
            className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
          />
        </div>
        <div>
          <Label htmlFor="zipCode" className="text-gray-700">
            Zip Code <span className="text-red-500">*</span>
          </Label>
          <Input
            id="add_patient_zip_code"
            value={formData.zipCode}
            onChange={(e) => updateFormData("zipCode", e.target.value)}
            placeholder="Enter zip code"
            maxLength={10}
            className="mt-1 bg-purple-50 border-purple-200 focus:border-purple-400"
          />
        </div>
        <div>
          <Label htmlFor="country" className="text-gray-700">
            Country
          </Label>
          <Select value={formData.country} onValueChange={(value) => updateFormData("country", value)}>
            <SelectTrigger id="add_patient_country" className="mt-1 bg-purple-100 border-purple-300">
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              <SelectItem value="USA">USA</SelectItem>
              <SelectItem value="Canada">Canada</SelectItem>
              <SelectItem value="Mexico">Mexico</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

// Step 3: Summary
const SummaryStep = ({ formData }: { formData: PatientFormData }) => {
  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-900">Review Patient Information</h2>

      {/* Personal Information Summary */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-purple-100 overflow-hidden">
        <h3 className="text-lg font-semibold text-pink-600 mb-4 flex items-center gap-2">
          <User className="h-5 w-5" />
          Personal Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="min-w-0">
            <span className="text-gray-500">First Name:</span>
            <p className="font-medium break-words">{formData.firstName || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Last Name:</span>
            <p className="font-medium break-words">{formData.lastName || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Alias Name:</span>
            <p className="font-medium break-words">{formData.aliasName || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Email:</span>
            <p className="font-medium break-words">{formData.email || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Phone Number:</span>
            <p className="font-medium break-words">{formData.phoneNumber ? `+1 ${formData.phoneNumber}` : "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Birth Date:</span>
            <p className="font-medium break-words">{formData.birthDate || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Gender:</span>
            <p className="font-medium break-words">{formData.gender || "-"}</p>
          </div>
        </div>
      </div>

      {/* Medical Records Summary */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-purple-100 overflow-hidden">
        <h3 className="text-lg font-semibold text-pink-600 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Medical Records
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="min-w-0">
            <span className="text-gray-500">EMR ID:</span>
            <p className="font-medium break-words">{formData.emrId || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Primary Insurance ID:</span>
            <p className="font-medium break-words">{formData.primaryInsuranceId || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Primary Insurance Payer:</span>
            <p className="font-medium break-words">{formData.primaryInsurancePayer || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Secondary Insurance ID:</span>
            <p className="font-medium break-words">{formData.secondaryInsuranceId || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Secondary Insurance Payer:</span>
            <p className="font-medium break-words">{formData.secondaryInsurancePayer || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Insurance Plan:</span>
            <p className="font-medium break-words">{formData.insurancePlan || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Program Eligibility:</span>
            <p className="font-medium break-words">
              {[
                formData.isCcmEligible && "CCM",
                formData.isRpmEligible && "RPM",
                formData.isPcmEligible && "PCM"
              ].filter(Boolean).join(", ") || "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Emergency Contact Information Summary */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-purple-100 overflow-hidden">
        <h3 className="text-lg font-semibold text-pink-600 mb-4 flex items-center gap-2">
          <Phone className="h-5 w-5" />
          Emergency Contact Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="min-w-0">
            <span className="text-gray-500">First Name:</span>
            <p className="font-medium break-words">{formData.emergencyFirstName || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Last Name:</span>
            <p className="font-medium break-words">{formData.emergencyLastName || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Phone Number:</span>
            <p className="font-medium break-words">{formData.emergencyPhoneNumber ? `+1 ${formData.emergencyPhoneNumber}` : "-"}</p>
          </div>
        </div>
      </div>

      {/* Address Summary */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-purple-100 overflow-hidden">
        <h3 className="text-lg font-semibold text-pink-600 mb-4">Address</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="min-w-0">
            <span className="text-gray-500">Address Line 1:</span>
            <p className="font-medium break-words">{formData.addressLine1 || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Address Line 2:</span>
            <p className="font-medium break-words">{formData.addressLine2 || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">City:</span>
            <p className="font-medium break-words">{formData.city || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">State:</span>
            <p className="font-medium break-words">{formData.state || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Zip Code:</span>
            <p className="font-medium break-words">{formData.zipCode || "-"}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500">Country:</span>
            <p className="font-medium break-words">{formData.country || "-"}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPatientPage;
